import type { BrowserOptions } from "@sentry/astro";

// Shared Sentry values for the browser SDK and the Worker.
//
// The DSN is a PUBLIC value: it permits one operation — submitting an event to
// the `blog-martide` project — and grants no read access and no project
// administration. It is supplied at BUILD time, because this site is
// `output: "static"` and has no server render that could hand it to a page at
// request time.
//
// Keeping the read in one module, rather than once per SDK config, stops the
// browser and the Worker reporting to two different Sentry projects.
//
// The literal is the fallback rather than a repository variable being the only
// source, because an unset variable would leave both SDKs inert and silent — the
// worst way for an error tracker to fail. `PUBLIC_SENTRY_DSN` still overrides it,
// so a developer can point a local build at their own project.
const BLOG_SENTRY_DSN =
  "https://64a8f30135ecbdd5a687d449e66cc123@o73733.ingest.us.sentry.io/4511904563920896";

// `||`, not `??`. The two build paths supply an unset variable differently: Vite
// leaves `import.meta.env.PUBLIC_SENTRY_DSN` undefined for the browser, while
// esbuild's `define` in scripts/bundle-worker-entry.mjs substitutes the empty
// string for the Worker. `??` catches only null and undefined, so it would leave
// the Worker with an empty DSN and no reporting at all.
export const SENTRY_DSN: string =
  import.meta.env.PUBLIC_SENTRY_DSN || BLOG_SENTRY_DSN;

// Sentry's own default environment is "production". A developer running
// `astro build` locally would then file events against the same environment the
// live site alerts on, so default to "development" instead and let each deploy
// name itself: `PUBLIC_SENTRY_ENVIRONMENT` at build time for the browser,
// the `SENTRY_ENVIRONMENT` Worker var at runtime for the Worker.
export const DEFAULT_SENTRY_ENVIRONMENT = "development";

/**
 * Resolve the Sentry environment name from a build-time or runtime value.
 *
 * An unset, empty or whitespace-only value falls back to
 * {@link DEFAULT_SENTRY_ENVIRONMENT}, so a deploy that forgets to set the
 * variable is visibly "development" rather than silently "production".
 */
export function resolveSentryEnvironment(value?: string | null): string {
  return value?.trim() || DEFAULT_SENTRY_ENVIRONMENT;
}

// Sentry 10's default deny patterns. The Sentry 11 migration guide applies the
// same list to header names and to URL query parameter names to keep the
// Sentry 10 behaviour, and this file does the same.
const SENTRY_V10_DENY_PATTERNS = [
  "forwarded",
  "-ip",
  "remote-",
  "via",
  "-user",
];

/**
 * What both SDKs may attach to an event.
 *
 * Sentry 11 replaced `sendDefaultPii` with this option and made an unset value
 * collect user info, cookies, headers and bodies. Readers of this site are not
 * logged in and nothing here needs to identify them, so this keeps the
 * restrictive Sentry 10 baseline. Both halves must pass it: leaving it out of
 * either one silently widens what that half collects.
 *
 * The Worker reads it from plain JavaScript, so src/lib/sentry.test.ts also
 * checks it against the option type of @sentry/cloudflare.
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: SENTRY_V10_DENY_PATTERNS },
    response: { deny: SENTRY_V10_DENY_PATTERNS },
  },
  httpBodies: [],
  urlQueryParams: { deny: SENTRY_V10_DENY_PATTERNS },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
} satisfies NonNullable<BrowserOptions["dataCollection"]>;
